import type { AudioRecorderAdapter, RecorderState, RecorderFrame } from '@repo/commons/models'

export function createWebAudioAdapter(): AudioRecorderAdapter {
	let state: RecorderState = 'idle'

	let frameCb: ((frame: RecorderFrame) => void) | null = null
	let stateCb: ((state: RecorderState) => void) | null = null
	let chunkCb: ((chunk: Blob) => void) | null = null

	let audioContext: AudioContext | null = null
	let analyser: AnalyserNode | null = null
	let stream: MediaStream | null = null
	let buffer: Uint8Array | null = null
	let rafId: number | null = null

	let mediaRecorder: MediaRecorder | null = null

	let startedAt = 0
	let pausedAt = 0
	let pauseAccumulated = 0

	// throttling (~12 FPS)
	let lastEmit = 0
	const EMIT_INTERVAL = 80 // ms

	const setState = (next: RecorderState) => {
		state = next
		stateCb?.(next)
	}

	const stopTracks = () => {
		stream?.getTracks().forEach((t) => t.stop())
		stream = null
	}

	const cancelRAF = () => {
		if (rafId != null) cancelAnimationFrame(rafId)
		rafId = null
	}

	const tick = () => {
		if (state !== 'recording' || !analyser || !buffer) return

		analyser.getByteTimeDomainData(buffer)

		// RMS loudness calculation
		let sumSquares = 0
		for (let i = 0; i < buffer.length; i++) {
			const v = (buffer[i] - 128) / 128
			sumSquares += v * v
		}

		const level = Math.sqrt(sumSquares / buffer.length) // 0..1
		const now = performance.now()

		if (now - lastEmit >= EMIT_INTERVAL) {
			const elapsedMs = now - startedAt - pauseAccumulated

			frameCb?.({
				elapsedMs,
				level
			})

			lastEmit = now
		}

		rafId = requestAnimationFrame(tick)
	}

	return {
		onFrame(cb) {
			frameCb = cb
		},

		onState(cb) {
			stateCb = cb
		},

		onChunk(cb) {
			chunkCb = cb
		},

		async start() {
			if (state === 'recording') return

			try {
				stream = await navigator.mediaDevices.getUserMedia({ audio: true })

				audioContext = new AudioContext()
				const source = audioContext.createMediaStreamSource(stream)

				analyser = audioContext.createAnalyser()
				analyser.fftSize = 1024 // better RMS stability

				source.connect(analyser)

				buffer = new Uint8Array(analyser.frequencyBinCount)

				mediaRecorder = new MediaRecorder(stream, {
					mimeType: 'audio/webm'
				})

				mediaRecorder.ondataavailable = (e) => {
					if (e.data.size > 0) {
						chunkCb?.(e.data)
					}
				}

				mediaRecorder.start(250) // emit chunk every 250ms

				startedAt = performance.now()
				pauseAccumulated = 0
				lastEmit = 0

				setState('recording')
				tick()
			} catch (err) {
				setState('error')
				throw err
			}
		},

		async pause() {
			if (state !== 'recording' || !audioContext) return

			pausedAt = performance.now()
			await audioContext.suspend()

			mediaRecorder?.pause()
			setState('paused')
			cancelRAF()
		},

		async resume() {
			if (state !== 'paused' || !audioContext) return

			pauseAccumulated += performance.now() - pausedAt
			await audioContext.resume()

			mediaRecorder?.resume()
			setState('recording')
			tick()
		},

		async stop() {
			if (state === 'idle') return

			setState('idle')
			cancelRAF()

			mediaRecorder?.stop()
			mediaRecorder = null

			await audioContext?.close().catch(() => {})
			audioContext = null
			analyser = null
			buffer = null

			stopTracks()
		},

		async reset() {
			setState('idle')
			cancelRAF()

			mediaRecorder?.stop()
			mediaRecorder = null

			await audioContext?.close().catch(() => {})
			audioContext = null
			analyser = null
			buffer = null

			stopTracks()

			startedAt = 0
			pausedAt = 0
			pauseAccumulated = 0
			lastEmit = 0
		},

		destroy() {
			this.reset()
			frameCb = null
			stateCb = null
			chunkCb = null
		}
	}
}
